import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Table } from '../../ui';
import {
  Card, Descriptions, Tag, Button, Space, Typography, Row, Col, Statistic, Tabs,
  Switch, Input, EmptyState, Image, Modal, Select, Spinner, Result, Popconfirm, Tooltip, theme, message, Upload,
} from '../../ui';
import {
  Pencil, PlayCircle, CheckCircle2, Ban, Users,
  Image as ImageIcon, Trash2, UserPlus, Flag, Upload as UploadIcon,
} from 'lucide-react';
import { api, request } from '../../api/client';
import { usePermission } from '../../store/auth';
import { brand } from '../../assets/brand';
import { formatIst } from '../../lib/time';
import { EventFormModal } from './EventFormModal';

const STATUS_COLOR: Record<string, string> = { PLANNED: 'blue', ONGOING: 'gold', COMPLETED: 'green', CANCELLED: 'red' };
const TYPE_LABEL: Record<string, string> = {
  RETAILER_MEET: 'Retailer meet', FARMER_MEET: 'Farmer meet', TRAINING: 'Training', SEMINAR: 'Seminar',
  PRODUCT_LAUNCH: 'Product launch', FIELD_DAY: 'Field day', MEETING: 'Meeting', OTHER: 'Other',
};
const statusLabel = (s: string) => (s ?? '').replace(/_/g, ' ');
const inr = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const NEXT: Record<string, string[]> = { PLANNED: ['ONGOING', 'COMPLETED', 'CANCELLED'], ONGOING: ['COMPLETED', 'CANCELLED'] };
const ACTION = (s: string) => (s === 'ONGOING' ? 'Start meet' : s === 'COMPLETED' ? 'Mark completed' : 'Cancel');

export function EventDetailPage() {
  const { token } = theme.useToken();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canEdit = usePermission('EVENT.EDIT');
  const [editOpen, setEditOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<any[]>([]);

  const { data: ev, isLoading, isError } = useQuery({ queryKey: ['event', id], queryFn: () => request<any>({ url: `/events/${id}` }), enabled: !!id });
  const { data: participants } = useQuery({ queryKey: ['event-participants', id], queryFn: () => request<any[]>({ url: `/events/${id}/participants` }), enabled: !!id });
  const { data: photos } = useQuery({ queryKey: ['event-photos', id], queryFn: () => request<any[]>({ url: `/events/${id}/photos` }), enabled: !!id });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['event', id] });
    qc.invalidateQueries({ queryKey: ['event-participants', id] });
    qc.invalidateQueries({ queryKey: ['events'] });
    qc.invalidateQueries({ queryKey: ['badges'] });
  };

  const change = useMutation({
    mutationFn: (status: string) => request({ url: `/events/${id}/status`, method: 'PATCH', data: { status } }),
    onSuccess: () => { message.success('Event updated'); invalidate(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  const setAttendance = useMutation({
    mutationFn: ({ partId, attended }: { partId: string; attended: boolean }) =>
      request({ url: `/events/${id}/participants/${partId}/attendance`, method: 'PATCH', data: { attended } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['event-participants', id] }),
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  const removePart = useMutation({
    mutationFn: (partId: string) => request({ url: `/events/${id}/participants/${partId}`, method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['event-participants', id] }); qc.invalidateQueries({ queryKey: ['event', id] }); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  const autoFill = useMutation({
    mutationFn: () => request<any[]>({ url: `/events/${id}/participants/auto-fill`, method: 'POST' }),
    onSuccess: (list: any) => {
      const n = Array.isArray(list) ? list.length : 0;
      message.success(`Territory invited — ${n} participant${n === 1 ? '' : 's'} on the list`);
      qc.invalidateQueries({ queryKey: ['event-participants', id] });
      qc.invalidateQueries({ queryKey: ['event', id] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  const afterBulk = () => { setSelectedKeys([]); qc.invalidateQueries({ queryKey: ['event-participants', id] }); qc.invalidateQueries({ queryKey: ['event', id] }); };
  const bulkAttend = useMutation({
    mutationFn: (attended: boolean) => request({ url: `/events/${id}/participants/bulk-attendance`, method: 'POST', data: { ids: selectedKeys, attended } }),
    onSuccess: (_d, attended) => { message.success(attended ? `Marked ${selectedKeys.length} attended` : `Marked ${selectedKeys.length} not attended`); afterBulk(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  const bulkRemove = useMutation({
    mutationFn: () => request({ url: `/events/${id}/participants/bulk-remove`, method: 'POST', data: { ids: selectedKeys } }),
    onSuccess: () => { message.success(`Removed ${selectedKeys.length} participant(s)`); afterBulk(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  const addPhotos = useMutation({
    mutationFn: (urls: string[]) => request({ url: `/events/${id}/photos`, method: 'POST', data: { urls } }),
    onSuccess: (_d, urls) => { message.success(`${urls.length} photo${urls.length === 1 ? '' : 's'} added`); qc.invalidateQueries({ queryKey: ['event-photos', id] }); qc.invalidateQueries({ queryKey: ['event', id] }); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  const removePhoto = useMutation({
    mutationFn: (photoId: string) => request({ url: `/events/${id}/photos/${photoId}`, method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['event-photos', id] }); qc.invalidateQueries({ queryKey: ['event', id] }); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  if (isLoading) return <div className="flex items-center justify-center gap-2 py-10 text-muted"><Spinner /> Loading…</div>;
  if (isError || !ev) return <Result status="error" title="Couldn't load this event" extra={<Button variant="primary" onClick={() => navigate('/events')}>Back to events</Button>} />;

  const transitions = (NEXT[ev.status] ?? []);

  return (
    <div>
      <Card className="mb-3">
        <Row align="middle" gutter={16}>
          <Col flex="auto">
            <Typography.Title level={4} style={{ margin: 0 }}>
              {ev.title} <Tag tone={STATUS_COLOR[ev.status] as any} style={{ marginLeft: 8 }}>{statusLabel(ev.status)}</Tag>
            </Typography.Title>
            <Typography.Text type="secondary">
              {ev.code} · {TYPE_LABEL[ev.type] ?? ev.type}{ev.cadence !== 'ONE_TIME' ? ` · ${ev.cadence.toLowerCase()}` : ''} · {formatIst(ev.startAt)}
              {ev.territoryName ? ` · ${ev.territoryName}` : ''}{ev.organiserName ? ` · by ${ev.organiserName}` : ''}
            </Typography.Text>
          </Col>
          {canEdit && (
            <Col flex="none">
              <Space wrap>
                {ev.status !== 'CANCELLED' && ev.status !== 'COMPLETED' && (
                  <Button variant="default" icon={<Pencil size={16} />} onClick={() => setEditOpen(true)}>Edit</Button>
                )}
                {transitions.map((s) =>
                  s === 'CANCELLED' ? (
                    <Popconfirm key={s} title="Cancel this event?" okText="Cancel event" okButtonProps={{ danger: true }} onConfirm={() => change.mutate(s)}>
                      <Button variant="danger" icon={<Ban size={16} />} loading={change.isPending && change.variables === s}>Cancel</Button>
                    </Popconfirm>
                  ) : (
                    <Button key={s} variant="primary" icon={s === 'ONGOING' ? <PlayCircle size={16} /> : <CheckCircle2 size={16} />}
                      onClick={() => change.mutate(s)} loading={change.isPending && change.variables === s}>
                      {ACTION(s)}
                    </Button>
                  ),
                )}
              </Space>
            </Col>
          )}
        </Row>
      </Card>

      <Row gutter={12} style={{ marginBottom: 12 }}>
        <Col xs={12} md={6}><Card><Statistic title="Invited" value={ev.invitedCount ?? 0} prefix={<Users size={16} />} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="Attended" value={ev.attendedCount ?? 0} valueStyle={{ color: '#52C41A' }} prefix={<Flag size={16} />} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="Photos" value={ev.photoCount ?? 0} prefix={<ImageIcon size={16} />} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="Expected" value={ev.capacity ?? 0} valueStyle={{ color: brand.primary }} /></Card></Col>
      </Row>

      <Card>
        <Tabs
          items={[
            {
              key: 'overview', label: 'Overview',
              children: (
                <Descriptions bordered column={2} size="small">
                  <Descriptions.Item label="Type">{TYPE_LABEL[ev.type] ?? ev.type}</Descriptions.Item>
                  <Descriptions.Item label="Cadence">{(ev.cadence ?? 'ONE_TIME').toLowerCase()}</Descriptions.Item>
                  <Descriptions.Item label="Territory">{ev.territoryName ?? '—'}</Descriptions.Item>
                  <Descriptions.Item label="Organiser">{ev.organiserName ?? '—'}</Descriptions.Item>
                  <Descriptions.Item label="Starts">{formatIst(ev.startAt)}</Descriptions.Item>
                  <Descriptions.Item label="Ends">{ev.endAt ? formatIst(ev.endAt) : '—'}</Descriptions.Item>
                  <Descriptions.Item label="Venue">{ev.venue ?? '—'}</Descriptions.Item>
                  <Descriptions.Item label="Expected attendees">{ev.capacity ?? 0}</Descriptions.Item>
                  <Descriptions.Item label="Budget">{ev.budget != null ? inr(ev.budget) : '—'}</Descriptions.Item>
                  <Descriptions.Item label="Completed at">{ev.completedAt ? formatIst(ev.completedAt) : '—'}</Descriptions.Item>
                  <Descriptions.Item label="Agenda / about" span={2}>{ev.description ?? '—'}</Descriptions.Item>
                </Descriptions>
              ),
            },
            {
              key: 'participants', label: `Participants (${ev.invitedCount ?? 0})`,
              children: (
                <>
                  {canEdit && (
                    <Space style={{ marginBottom: 12 }} wrap>
                      <Button variant="primary" icon={<UserPlus size={16} />} onClick={() => setAddOpen(true)}>Add participants</Button>
                      <Popconfirm
                        title="Invite the whole territory?"
                        description="Adds every retailer & farmer of this event's territory (and its sub-areas) to the list."
                        okText="Invite all" onConfirm={() => autoFill.mutate()}
                      >
                        <Tooltip label="New events auto-invite the territory; use this to re-sync or fill an older event.">
                          <Button variant="default" icon={<Users size={16} />} loading={autoFill.isPending}>Auto-invite territory</Button>
                        </Tooltip>
                      </Popconfirm>
                      {selectedKeys.length > 0 && (
                        <>
                          <span style={{ color: token.colorTextTertiary }}>·</span>
                          <Button variant="default" icon={<Flag size={16} />} loading={bulkAttend.isPending} onClick={() => bulkAttend.mutate(true)}>Mark attended ({selectedKeys.length})</Button>
                          <Button variant="default" loading={bulkAttend.isPending} onClick={() => bulkAttend.mutate(false)}>Mark not attended</Button>
                          <Popconfirm title={`Remove ${selectedKeys.length} participant(s)?`} okText="Remove" okButtonProps={{ danger: true }} onConfirm={() => bulkRemove.mutate()}>
                            <Button variant="danger" icon={<Trash2 size={16} />} loading={bulkRemove.isPending}>Remove ({selectedKeys.length})</Button>
                          </Popconfirm>
                        </>
                      )}
                    </Space>
                  )}
                  <Table
                    rowKey="id" size="small" pagination={false} dataSource={participants ?? []}
                    rowSelection={canEdit ? { selectedRowKeys: selectedKeys, onChange: (keys) => setSelectedKeys(keys) } : undefined}
                    locale={{ emptyText: <EmptyState title="No invitees yet — add the retailers & farmers for this meet." /> }}
                    columns={[
                      { title: 'Name', render: (_: any, p: any) => (
                        <div><div style={{ fontWeight: 500 }}>{p.name}</div>
                          <div style={{ fontSize: 12, color: token.colorTextTertiary }}>{[p.code, p.place, p.mobile].filter(Boolean).join(' · ')}</div></div>
                      ) },
                      { title: 'Type', dataIndex: 'partyType', width: 110, render: (v: string) => <Tag tone={v === 'RETAILER' ? 'blue' : 'green'}>{v}</Tag> },
                      {
                        title: 'Attended', dataIndex: 'attended', width: 120,
                        render: (v: boolean, p: any) => canEdit
                          ? <Switch checked={v} onCheckedChange={(c) => setAttendance.mutate({ partId: p.id, attended: c })} />
                          : (v ? <Tag tone="green">Yes</Tag> : <Tag>No</Tag>),
                      },
                      ...(canEdit ? [{
                        title: '', width: 50,
                        render: (_: any, p: any) => (
                          <Popconfirm title="Remove this participant?" onConfirm={() => removePart.mutate(p.id)}>
                            <Button size="sm" variant="soft-danger" icon={<Trash2 size={16} />} aria-label="Remove participant" />
                          </Popconfirm>
                        ),
                      } as any] : []),
                    ]}
                  />
                </>
              ),
            },
            {
              key: 'outcome', label: 'Outcome & Photos',
              children: <OutcomeTab ev={ev} photos={photos ?? []} canEdit={canEdit} onAddPhotos={(urls) => addPhotos.mutate(urls)} onRemovePhoto={(pid) => removePhoto.mutate(pid)} onSaved={invalidate} />,
            },
          ]}
        />
      </Card>

      <EventFormModal open={editOpen} onClose={() => setEditOpen(false)} record={ev} />
      <AddParticipantsModal open={addOpen} onClose={() => setAddOpen(false)} eventId={id} territoryId={ev.territoryId} existing={participants ?? []} onDone={() => { setAddOpen(false); qc.invalidateQueries({ queryKey: ['event-participants', id] }); qc.invalidateQueries({ queryKey: ['event', id] }); }} />
    </div>
  );
}

// ── Outcome + photo gallery tab ───────────────────────────────────────────
function OutcomeTab({ ev, photos, canEdit, onAddPhotos, onRemovePhoto, onSaved }:
  { ev: any; photos: any[]; canEdit: boolean; onAddPhotos: (urls: string[]) => void; onRemovePhoto: (id: string) => void; onSaved: () => void }) {
  const [summary, setSummary] = useState(ev.outcomeSummary ?? '');
  const [decisions, setDecisions] = useState(ev.decisions ?? '');
  const [discussion, setDiscussion] = useState(ev.discussionNotes ?? '');
  useEffect(() => { setSummary(ev.outcomeSummary ?? ''); setDecisions(ev.decisions ?? ''); setDiscussion(ev.discussionNotes ?? ''); }, [ev.id, ev.outcomeSummary, ev.decisions, ev.discussionNotes]);

  const save = useMutation({
    mutationFn: () => request({ url: `/events/${ev.id}`, method: 'PATCH', data: { outcomeSummary: summary, decisions, discussionNotes: discussion } }),
    onSuccess: () => { message.success('Outcome saved'); onSaved(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  if (!canEdit) {
    return (
      <div>
        <Field label="Summary" value={ev.outcomeSummary} />
        <Field label="Decisions taken" value={ev.decisions} />
        <Field label="Discussion points" value={ev.discussionNotes} />
        <Typography.Title level={5} style={{ marginTop: 16 }}>Photos</Typography.Title>
        <Gallery photos={photos} />
      </div>
    );
  }

  return (
    <div>
      <Typography.Paragraph type="secondary" style={{ marginTop: -6 }}>
        Post what happened at the meet — a short summary, the decisions taken, and the key discussion points. Add photos to the gallery below.
      </Typography.Paragraph>
      <div style={{ marginBottom: 4, fontWeight: 500 }}>Summary</div>
      <Input.TextArea rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="What happened at the meet…" style={{ marginBottom: 12 }} />
      <div style={{ marginBottom: 4, fontWeight: 500 }}>Decisions taken</div>
      <Input.TextArea rows={3} value={decisions} onChange={(e) => setDecisions(e.target.value)} placeholder="Decisions / action items…" style={{ marginBottom: 12 }} />
      <div style={{ marginBottom: 4, fontWeight: 500 }}>Discussion points</div>
      <Input.TextArea rows={3} value={discussion} onChange={(e) => setDiscussion(e.target.value)} placeholder="What was discussed…" style={{ marginBottom: 12 }} />
      <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>Save outcome</Button>

      <Typography.Title level={5} style={{ marginTop: 20 }}>Photos ({photos.length})</Typography.Title>
      <div style={{ marginBottom: 12 }}>
        <MultiPhotoUpload onAdd={onAddPhotos} />
        <Typography.Text type="secondary" style={{ marginLeft: 10, fontSize: 12 }}>Select multiple images at once · JPG/PNG · up to 5 MB each</Typography.Text>
      </div>
      <Gallery photos={photos} onRemove={onRemovePhoto} />
    </div>
  );
}

// Multi-select photo uploader: each chosen image is uploaded to /uploads/event-photo, and the
// resulting URLs are batched (debounced) into a single "add photos" call.
function MultiPhotoUpload({ onAdd }: { onAdd: (urls: string[]) => void }) {
  const pending = useRef<string[]>([]);
  const timer = useRef<any>(null);
  const [busy, setBusy] = useState(false);
  const flush = () => { const urls = pending.current; pending.current = []; setBusy(false); if (urls.length) onAdd(urls); };
  const beforeUpload = async (file: any) => {
    if (!String(file.type || '').startsWith('image/')) { message.error(`${file.name}: images only`); return Upload.LIST_IGNORE; }
    if (file.size > 5 * 1024 * 1024) { message.error(`${file.name}: max 5 MB`); return Upload.LIST_IGNORE; }
    setBusy(true);
    try {
      const form = new FormData(); form.append('file', file);
      const r = await api.post('/uploads/event-photo', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = r.data?.data?.url;
      if (url) { pending.current.push(url); clearTimeout(timer.current); timer.current = setTimeout(flush, 600); }
    } catch { message.error(`${file.name}: upload failed`); }
    return Upload.LIST_IGNORE;
  };
  return (
    <Upload multiple showUploadList={false} accept="image/*" beforeUpload={beforeUpload}>
      <Button variant="default" icon={<UploadIcon size={16} />} loading={busy}>Add photos</Button>
    </Upload>
  );
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontWeight: 500, marginBottom: 2 }}>{label}</div>
      <div style={{ whiteSpace: 'pre-wrap', color: value ? undefined : '#bbb' }}>{value || 'Not posted yet'}</div>
    </div>
  );
}

function Gallery({ photos, onRemove }: { photos: any[]; onRemove?: (id: string) => void }) {
  if (!photos.length) return <EmptyState title="No photos yet" />;
  return (
    <Image.PreviewGroup>
      <Space wrap size={12}>
        {photos.map((p) => (
          <div key={p.id} style={{ position: 'relative' }}>
            <Image src={p.url} width={140} height={140} style={{ objectFit: 'cover', borderRadius: 8 }} />
            {onRemove && (
              <Popconfirm title="Remove this photo?" onConfirm={() => onRemove(p.id)}>
                <Button size="sm" variant="danger" icon={<Trash2 size={16} />} style={{ position: 'absolute', top: 4, right: 4 }} aria-label="Remove photo" />
              </Popconfirm>
            )}
          </div>
        ))}
      </Space>
    </Image.PreviewGroup>
  );
}

// ── Add participants modal (retailers + farmers of the event's territory) ──
function AddParticipantsModal({ open, onClose, eventId, territoryId, existing, onDone }:
  { open: boolean; onClose: () => void; eventId: string; territoryId?: string | null; existing: any[]; onDone: () => void }) {
  const [retailerIds, setRetailerIds] = useState<string[]>([]);
  const [farmerIds, setFarmerIds] = useState<string[]>([]);

  useEffect(() => { if (open) { setRetailerIds([]); setFarmerIds([]); } }, [open]);

  const { data: retailers } = useQuery({
    queryKey: ['event-pick-retailers', territoryId],
    queryFn: () => request<any>({ url: '/retailers', params: { territoryId: territoryId ?? undefined, pageSize: 500 } }),
    enabled: open,
  });
  const { data: farmers } = useQuery({
    queryKey: ['event-pick-farmers', territoryId],
    queryFn: () => request<any>({ url: '/farmers', params: { territoryId: territoryId ?? undefined, pageSize: 500 } }),
    enabled: open,
  });

  const haveR = useMemo(() => new Set(existing.filter((e) => e.partyType === 'RETAILER').map((e) => String(e.retailerId))), [existing]);
  const haveF = useMemo(() => new Set(existing.filter((e) => e.partyType === 'FARMER').map((e) => String(e.farmerId))), [existing]);
  const retailerOpts = useMemo(() => (retailers?.data ?? []).filter((r: any) => !haveR.has(String(r.id))).map((r: any) => ({ value: String(r.id), label: `${r.companyName}${r.code ? ` (${r.code})` : ''}${r.city ? ` · ${r.city}` : ''}` })), [retailers, haveR]);
  const farmerOpts = useMemo(() => (farmers?.data ?? []).filter((f: any) => !haveF.has(String(f.id))).map((f: any) => ({ value: String(f.id), label: `${f.fullName ?? f.name}${f.mobile ? ` · ${f.mobile}` : ''}${f.city ? ` · ${f.city}` : ''}` })), [farmers, haveF]);

  const add = useMutation({
    mutationFn: () => request({ url: `/events/${eventId}/participants`, method: 'POST', data: { retailerIds, farmerIds } }),
    onSuccess: () => { message.success('Participants added'); onDone(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to add'),
  });

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }}
      title="Add participants" width={620}
      footer={<>
        <Button variant="default" onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={add.isPending} disabled={!retailerIds.length && !farmerIds.length} onClick={() => add.mutate()}>Add</Button>
      </>}>
      <Typography.Paragraph type="secondary" style={{ marginTop: -6 }}>
        Only retailers &amp; farmers of this event's territory are shown. Already-invited ones are hidden.
      </Typography.Paragraph>
      <div style={{ marginBottom: 4, fontWeight: 500 }}>Retailers</div>
      <Select mode="multiple" allowClear showSearch optionFilterProp="label" style={{ width: '100%', marginBottom: 12 }}
        placeholder="Select retailers" value={retailerIds} onChange={setRetailerIds} options={retailerOpts} maxTagCount={1} />
      <div style={{ marginBottom: 4, fontWeight: 500 }}>Farmers</div>
      <Select mode="multiple" allowClear showSearch optionFilterProp="label" style={{ width: '100%' }}
        placeholder="Select farmers" value={farmerIds} onChange={setFarmerIds} options={farmerOpts} maxTagCount={1} />
    </Modal>
  );
}
